
import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { UserRole, User } from '../types';
import { WinBaghrafLogo } from './UI';
import { ROUTES } from '../routes';
import { 
  FileText, 
  Menu, 
  X, 
  LogOut,
  UserCircle,
  TrendingUp,
  Settings,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  MoreHorizontal,
  KeyRound
} from 'lucide-react';

// Local storage key for sidebar state
const SIDEBAR_COLLAPSED_KEY = 'wbb-sidebar-collapsed';

interface LayoutProps {
  currentUser: User;
  onSwitchRole?: (role: UserRole) => void; // Optional, dev-only
  onLogout: () => void;
}

export const Layout: React.FC<LayoutProps> = ({ 
  currentUser, 
  onSwitchRole, 
  onLogout
}) => {
  const [isMobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return stored === 'true';
  });
  const [isMobileMoreOpen, setMobileMoreOpen] = useState(false);
  const location = useLocation();

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed));
  }, [isCollapsed]);

  // Auto-collapse sidebar on mobile when navigating
  useEffect(() => {
    setMobileSidebarOpen(false);
    setMobileMoreOpen(false);
  }, [location.pathname]);

  // Close mobile sidebar on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileSidebarOpen(false);
        setMobileMoreOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navItems = [
    { 
      label: 'Manajemen Transaksi', 
      shortLabel: 'Transaksi',
      path: ROUTES.TRANSACTIONS, 
      icon: CheckCircle2,
      roles: [UserRole.ADMINISTRATOR, UserRole.STAF_ADMIN],
      showInBottomNav: true
    },
    { 
      label: 'Saldo Bank/Kas Harian', 
      shortLabel: 'Bank/Kas',
      path: ROUTES.STATEMENT_RECORDER, 
      icon: FileText,
      roles: [UserRole.ADMINISTRATOR, UserRole.STAF_ADMIN, UserRole.STAF_HO, UserRole.STAF_FINANCE],
      showInBottomNav: true
    },
    { 
      label: 'Saldo Reksadana Harian', 
      shortLabel: 'Reksadana',
      path: ROUTES.MUTUAL_FUND_RECORDER, 
      icon: TrendingUp,
      roles: [UserRole.ADMINISTRATOR, UserRole.STAF_ADMIN, UserRole.STAF_HO, UserRole.STAF_FINANCE],
      showInBottomNav: true
    },
    { 
      label: 'Pengaturan Akun', 
      shortLabel: 'Akun',
      path: ROUTES.USER_SETTINGS, 
      icon: KeyRound,
      roles: [UserRole.STAF_ADMIN, UserRole.STAF_HO, UserRole.STAF_FINANCE],
      showInBottomNav: false
    },
    { 
      label: 'Pengaturan Sistem', 
      shortLabel: 'Pengaturan',
      path: ROUTES.SETTINGS, 
      icon: Settings,
      roles: [UserRole.ADMINISTRATOR, UserRole.STAF_ADMIN],
      showInBottomNav: false
    }
  ];

  const filteredNav = navItems.filter(item => item.roles.includes(currentUser.role));
  const bottomNavItems = filteredNav.filter(item => item.showInBottomNav).slice(0, 3);
  const moreNavItems = filteredNav.filter(item => !item.showInBottomNav || filteredNav.filter(i => i.showInBottomNav).indexOf(item) >= 3);

  const getHeaderTitle = () => {
    const path = location.pathname;
    if (path.startsWith('/statements')) return 'Saldo Bank/Kas';
    if (path.startsWith('/mutual-funds')) return 'Saldo Reksadana';
    if (path === '/transactions') return 'Transaksi';
    if (path === '/my-settings') return 'Pengaturan Akun';
    if (path === '/setting') return 'Pengaturan';
    if (path === '/setting/companies') return 'Perusahaan';
    if (path.startsWith('/setting/companies/')) return 'Detail Perusahaan';
    if (path === '/setting/users') return 'Pengguna';
    if (path.startsWith('/setting')) return 'Pengaturan';
    return 'WBB Group';
  };

  const toggleCollapse = () => setIsCollapsed(prev => !prev);

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden font-sans">
      {/* Mobile Sidebar Overlay */}
      {isMobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-40 lg:hidden transition-opacity"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Mobile More Menu Overlay */}
      {isMobileMoreOpen && (
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setMobileMoreOpen(false)}
        />
      )}

      {/* Desktop Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 bg-[#0F172A] text-slate-300 flex flex-col transform transition-all duration-300 ease-in-out border-r border-slate-800 shadow-xl
        ${isMobileSidebarOpen ? 'translate-x-0 w-72' : '-translate-x-full w-72'}
        lg:relative lg:translate-x-0 lg:shadow-none
        ${isCollapsed ? 'lg:w-20' : 'lg:w-72'}
      `}>
        {/* Logo Section */}
        <div className={`flex items-center h-28 border-b border-slate-800/50 shrink-0 relative bg-white/5 ${isCollapsed ? 'justify-center px-2' : 'justify-center'}`}>
          <div className={`transform origin-center flex flex-col items-center ${isCollapsed ? 'scale-60' : 'scale-75'}`}>
            <WinBaghrafLogo className="w-12 h-10" showText={false} />
            {!isCollapsed && (
              <div className="text-white font-serif tracking-widest text-lg font-bold mt-2">WBB Group</div>
            )}
          </div>
          {/* Mobile close button */}
          <button 
            onClick={() => setMobileSidebarOpen(false)} 
            className="lg:hidden absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className={`flex-1 overflow-y-auto custom-scrollbar ${isCollapsed ? 'p-2' : 'p-4'} space-y-1`}>
          {!isCollapsed && (
            <p className="px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 mt-2">Menu</p>
          )}
          {filteredNav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.label}
                to={item.path}
                title={isCollapsed ? item.label : undefined}
                className={({ isActive }) => `
                  flex items-center w-full ${isCollapsed ? 'justify-center px-2' : 'px-4'} py-3 text-sm font-medium rounded-xl transition-all duration-200 group
                  ${isActive 
                    ? 'bg-indigo-600/10 text-indigo-400 ring-1 ring-indigo-500/20 shadow-sm' 
                    : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-100'}
                `}
              >
                {({ isActive }) => (
                  <>
                    <Icon className={`w-5 h-5 ${isCollapsed ? '' : 'mr-3'} transition-colors ${isActive ? 'text-indigo-400' : 'text-slate-500 group-hover:text-slate-300'}`} />
                    {!isCollapsed && item.label}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* User Section */}
        <div className={`border-t border-slate-800 bg-[#0F172A] shrink-0 ${isCollapsed ? 'p-2' : 'p-4'}`}>
           {/* User Info */}
           {!isCollapsed ? (
             <div className="flex items-center space-x-3 mb-6 p-2 rounded-xl bg-slate-800/50 border border-slate-700/50">
                <div className="bg-slate-700 p-2 rounded-lg">
                  <UserCircle className="w-5 h-5 text-slate-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{currentUser.name}</p>
                  <p className="text-xs text-slate-400 truncate capitalize">{currentUser.role.replace('_', ' ').toLowerCase()}</p>
                </div>
             </div>
           ) : (
             <div className="flex justify-center mb-4">
               <div className="bg-slate-700 p-2 rounded-lg" title={currentUser.name}>
                 <UserCircle className="w-5 h-5 text-slate-300" />
               </div>
             </div>
           )}
           
           {/* Role Switcher - DEV ONLY (hidden when collapsed) */}
           {!isCollapsed && import.meta.env.DEV && onSwitchRole && (
             <div className="mb-4">
               <label className="text-[10px] text-amber-500 uppercase font-bold px-1 block mb-2 tracking-wider">⚠️ DEV: Ganti Peran</label>
               <select 
                  value={currentUser.role} 
                  onChange={(e) => onSwitchRole(e.target.value as UserRole)}
                  className="w-full bg-slate-900 text-xs text-slate-300 border border-amber-700/50 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500 hover:border-amber-600 transition-colors"
               >
                  <option value={UserRole.ADMINISTRATOR}>Administrator</option>
                  <option value={UserRole.STAF_ADMIN}>Staf Admin</option>
                  <option value={UserRole.STAF_HO}>Staf HO</option>
                  <option value={UserRole.STAF_FINANCE}>Staf Finance</option>
               </select>
             </div>
           )}
           
           {/* Logout Button */}
           <button 
             onClick={onLogout}
             title={isCollapsed ? 'Keluar' : undefined}
             className={`flex items-center w-full ${isCollapsed ? 'justify-center px-2' : 'px-4'} py-2 text-sm text-slate-400 hover:text-white transition-colors hover:bg-slate-800/50 rounded-lg`}
            >
              <LogOut className={`w-4 h-4 ${isCollapsed ? '' : 'mr-2'}`} />
              {!isCollapsed && 'Keluar'}
           </button>
        </div>

        {/* Desktop Collapse Toggle */}
        <button
          onClick={toggleCollapse}
          className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-slate-700 hover:bg-slate-600 rounded-full items-center justify-center text-slate-300 hover:text-white transition-colors shadow-lg border border-slate-600"
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden bg-white">
        {/* Mobile Header - Native App Style */}
        <header className="lg:hidden glass-header sticky top-0 z-30 border-b border-gray-100 pt-safe">
          <div className="flex items-center justify-between h-14 px-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
                <WinBaghrafLogo className="w-5 h-5" showText={false} />
              </div>
              <div>
                <h1 className="text-base font-bold text-gray-900">
                  {getHeaderTitle()}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setMobileSidebarOpen(true)}
                className="touch-target flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 active:bg-gray-200 transition-colors"
              >
                <UserCircle className="w-6 h-6" />
              </button>
            </div>
          </div>
        </header>

        {/* Desktop Header */}
        <header className="hidden lg:flex bg-white border-b border-gray-100 h-20 items-center justify-between px-4 sm:px-8 shrink-0 z-20">
          <div className="flex items-center flex-1">
             <div>
                <h1 className="text-2xl font-bold text-gray-900 capitalize tracking-tight">
                    {getHeaderTitle()}
                </h1>
                <p className="text-sm text-gray-500 mt-0.5">Selamat datang kembali, berikut perkembangan hari ini.</p>
             </div>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full border border-indigo-100">
               Beta v1.0
            </span>
          </div>
        </header>

        {/* Scrollable Content Area */}
        <main className="flex-1 overflow-y-auto scroll-smooth-touch scrollbar-hide bg-gray-50/50">
          {/* Mobile: Add padding for bottom nav */}
          <div className="p-4 lg:p-8 pb-bottom-nav lg:pb-12">
            <div className="max-w-7xl mx-auto space-y-6 lg:space-y-8">
              <Outlet />
            </div>
          </div>
        </main>

        {/* Mobile Bottom Navigation - Native App Style */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 pb-safe">
          <div className="flex items-center justify-around h-16">
            {bottomNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`flex flex-col items-center justify-center flex-1 h-full touch-target select-none-touch haptic-feedback transition-colors relative ${
                    isActive ? 'text-indigo-600' : 'text-gray-400'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-indigo-600' : 'text-gray-400'}`} />
                  <span className={`text-[10px] mt-1 font-medium ${isActive ? 'text-indigo-600' : 'text-gray-500'}`}>
                    {item.shortLabel}
                  </span>
                  {isActive && (
                    <div className="absolute bottom-1 w-1 h-1 rounded-full bg-indigo-600" />
                  )}
                </NavLink>
              );
            })}
            
            {/* More Button */}
            {moreNavItems.length > 0 && (
              <button
                onClick={() => setMobileMoreOpen(true)}
                className={`flex flex-col items-center justify-center flex-1 h-full touch-target select-none-touch haptic-feedback transition-colors ${
                  isMobileMoreOpen ? 'text-indigo-600' : 'text-gray-400'
                }`}
              >
                <MoreHorizontal className="w-5 h-5" />
                <span className="text-[10px] mt-1 font-medium text-gray-500">Lainnya</span>
              </button>
            )}
          </div>
        </nav>

        {/* Mobile More Menu - Bottom Sheet Style */}
        {isMobileMoreOpen && (
          <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-2xl bottom-sheet pb-safe">
            <div className="py-2">
              {/* User Info */}
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                    <span className="text-indigo-600 font-bold text-sm">
                      {currentUser.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{currentUser.name}</p>
                    <p className="text-xs text-gray-500 capitalize">{currentUser.role.replace('_', ' ').toLowerCase()}</p>
                  </div>
                </div>
              </div>

              {/* More Nav Items */}
              <div className="py-2">
                {moreNavItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                  
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMoreOpen(false)}
                      className={`flex items-center gap-4 px-4 py-3 list-item-native ${
                        isActive ? 'bg-indigo-50' : ''
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        isActive ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <p className={`font-medium ${isActive ? 'text-indigo-600' : 'text-gray-900'}`}>
                          {item.label}
                        </p>
                      </div>
                      <ChevronRight className={`w-5 h-5 ${isActive ? 'text-indigo-400' : 'text-gray-300'}`} />
                    </NavLink>
                  );
                })}
              </div>

              {/* Logout */}
              <div className="border-t border-gray-100 py-2">
                <button
                  onClick={() => {
                    setMobileMoreOpen(false);
                    onLogout();
                  }}
                  className="flex items-center gap-4 px-4 py-3 w-full list-item-native text-left"
                >
                  <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                    <LogOut className="w-5 h-5 text-red-500" />
                  </div>
                  <p className="font-medium text-red-600">Keluar</p>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
